import type { EngagementRecord } from "../api/engagements";

/** Hex SHA-256 of a UTF-8 string — matches backend hashlib.sha256(s.encode("utf-8")). */
export async function sha256Hex(text: string): Promise<string> {
  const bytes = new TextEncoder().encode(text);
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/** Hex SHA-256 of raw bytes (e.g. a dropped file's ArrayBuffer). */
export async function sha256HexBytes(buf: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", buf);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/**
 * Recompute the backend's is_script_approved (NOT serialized — a plain @property).
 * True iff an approval hash is stored AND equals the live script's hash.
 */
export function clientScriptApproved(
  rec: EngagementRecord,
  liveScriptSha256: string | null,
): boolean {
  const vc = rec.video_config;
  return (
    !!vc &&
    !!vc.approved_script_sha256 &&
    liveScriptSha256 !== null &&
    vc.approved_script_sha256 === liveScriptSha256
  );
}

/** Recompute is_video_clip_current (also a non-serialized @property). */
export function clientClipCurrent(
  rec: EngagementRecord,
  liveScriptSha256: string | null,
): boolean {
  const vr = rec.video_render;
  const vc = rec.video_config;
  return (
    clientScriptApproved(rec, liveScriptSha256) &&
    !!vr &&
    !!vr.clip_s3_key &&
    vr.rendered_script_sha256 === vc!.approved_script_sha256
  );
}

export type ScriptStage = "none" | "draft" | "approved" | "edited";
export type ConsentStage = "none" | "partial" | "complete";
export type RenderStage = "none" | "pending" | "ready" | "stale";
export type DispatchStage = "none" | "active" | "left";

export interface DrillStage {
  script: ScriptStage;
  consent: ConsentStage;
  render: RenderStage;
  dispatch: DispatchStage;
}

export function videoDrillStage(
  rec: EngagementRecord,
  liveScriptSha256: string | null,
): DrillStage {
  const vc = rec.video_config;
  const vr = rec.video_render;
  const vcon = rec.video_consent;
  const vd = rec.video_dispatch;

  let script: ScriptStage = "none";
  if (vc?.script) {
    if (!vc.approved_script_sha256) script = "draft";
    else if (clientScriptApproved(rec, liveScriptSha256)) script = "approved";
    else script = "edited";
  }

  const consentCount = vcon
    ? [vcon.likeness_release, vcon.org_sponsor_signoff, vcon.vendor_consent_record].filter(Boolean).length
    : 0;
  const consent: ConsentStage = consentCount === 0 ? "none" : consentCount === 3 ? "complete" : "partial";

  let render: RenderStage = "none";
  if (vr) {
    if (vr.status === "pending") render = "pending";
    else if (clientClipCurrent(rec, liveScriptSha256)) render = "ready";
    else render = "stale";
  }

  let dispatch: DispatchStage = "none";
  if (vd) dispatch = vd.status === "left" ? "left" : "active";

  return { script, consent, render, dispatch };
}

const VIDEO_ERROR_TEXT: Record<string, string> = {
  script_not_approved: "Approve the script before rendering.",
  video_clip_not_current: "Render a clip for the current approved script first.",
  engagement_not_video_channel: "This engagement is not a video engagement.",
  realtime_not_supported_slice4: "Realtime avatar mode isn't available yet.",
  no_render_in_progress: "No render in progress.",
  no_active_dispatch: "No active dispatch.",
};

export function mapVideoError(detail: string): string {
  if (detail in VIDEO_ERROR_TEXT) return VIDEO_ERROR_TEXT[detail];
  if (detail.startsWith("engagement_not_active_state_"))
    return "Activate the engagement before dispatching.";
  if (detail.startsWith("dispatch_rate_limited"))
    return "Just dispatched — wait a moment before retrying.";
  if (detail.toLowerCase().includes("not configured") || detail === "Service Unavailable")
    return "Video services aren't configured yet — contact the operator.";
  return `Action failed: ${detail}`;
}
